import type { ApiLoopFishInformation } from '../../../microApps/common/api/products/apiProductModels';
import type { EcomPromotion } from '../../../microApps/common/models/domain/ecommerce/promotion/promotion';
import type { PriceData } from '../../../microApps/common/models/priceData/priceData';

export const productComparativePriceUnits = [
    'kg',
    'liter',
    'disk',
    'meter',
    'styck',
    'tvätt',
] as const;

export type ProductComparativePriceUnit = (typeof productComparativePriceUnits)[number];

export type ProductComparativePrice = {
    priceData: PriceData;
    unit: ProductComparativePriceUnit | null;
    postfix: string | null;
    fallbackText: string;
};

export interface ApiProduct {
    ean: string;
    name: string;
    manufacturer: string;
    image?: ApiCloudinaryImage;
    priceData?: ApiPriceData;
    priceUnit: string;
    taxRate: number;
    comparativePrice?: ProductComparativePrice;
    promotionPriceData?: ApiPriceData;
    promo?: EcomPromotion;
    promos: ({ priority?: number } & EcomPromotion)[];
    details: ApiProductDetails;
    sections: ApiProductSection[][];
    url: string;
    id: string;
    variants: ApiProductVariant[];
    identifier: string;
    promotionProductsEans?: string[];
    isMagazine?: boolean;
    historicalPriceData?: ApiPriceData;
    consumerInstructions?: ApiConsumerInstructions;
    compulsoryAdditiveLabelInformation?: string;
    replacementCountries: ApiCountry[];
    originCountry?: ApiCountry;
    variety?: string;
    class?: string;
    preparationInstructionsList: string[];
    regulatedArticleDescription?: string;
    regulatedProductName?: string;
    animalFoodData?: ApiAnimalFoodData;
    displayWeightPrice: boolean;
    displayLowerPrice: boolean;
    /** Assortment pieceWeight (kg): weight per piece or order line; e.g. 0.5 for about 500 g. */
    pieceWeight?: number;
    energyLabel?: ApiProductEnergyLabel;
}

export interface ApiProductEnergyLabel {
    imageUrl?: string;
    smallImageUrl: string;
    energyClass: string;
    productSheetUrl?: string;
    accessibilityText: string;
}

interface ApiAnimalFoodTableRow {
    guidingAmount?: {
        displayName: string;
    };
    animalWeight?: {
        displayName: string;
    };
}

interface ApiAnimalFoodData {
    feedAdditiveStatement?: string;
    feedAnalyticalConstituentsStatement?: string;
    feedCompositionStatement?: string;
    feedType?: string;
    feedingInstructions?: string;
    targetedConsumptionBy?: { code: string; value: string };
    animalFeedingTable?: ApiAnimalFoodTableRow[];
}

interface ApiCloudinaryImage {
    url: string;
    path?: string;
    identifier?: string;
}

export interface ApiProductVariant {
    id: string;
    name: string;
    weight?: number;
    /** Assortment variance weight/volume, e.g. 0.1 with unit `kilogram` for ~100 g. */
    quantity?: number;
    unit?: string;
    priceData?: ApiPriceData;
    promotionPriceData?: ApiPriceData;
}

interface ApiNutrientInformation {
    header: {
        nutrientBasisQuantity: number;
        nutrientBasisQuantityType?: string;
        nutrientBasisQuantityUnit?: { code: string; value: string };
        preparationState?: { code: 'PREPARED'; value: string }; // there are more codes but we are not interested
        unitShortValue?: string;
    };
    // details - later on added by AS
}

export interface ApiFishInformation extends ApiLoopFishInformation {}

interface ApiProductDetails {
    description?: string;
    recycleFeeData?: ApiPriceData;
    isAgeLimited: boolean;
    isNicotineProduct: boolean;
    maximumStorageTemperature?: string;
    alcoholPercentage?: string;
    consumerInformationText?: string[];
    nutrientInformation?: ApiNutrientInformation[];
    nutritionFacts: ApiNutritionFact[];
    content?: string;
    attributes: ApiProductAttribute[];
    healthAttributes: ApiProductAttribute[];
    healthSafetyPrecautionaryStatementText: string[];
    healthSafetySignalWordsCode: string[];
    healthSafetySignalStatementText: string[];
    size: ApiProductSize;
    fromSweden: boolean;
    declarationOfOrigin?: string;
    countryOfOriginCodes?: string[];
    catchData?: ApiProductCatchData;
    fishReportingInformations?: ApiFishInformation[];
    nonFoodIngredientStatement?: string;
    storageStateCode?: {
        code: 'PREVIOUSLY_FROZEN' | 'NOT_PREVIOUSLY_FROZEN';
        value: string;
    };
    pharmaceuticalData?: {
        isPharmaceutical: boolean;
        pharmaceuticalType?: {
            code: string;
            value: string;
        };
    };
    sustainabilityInfo?: {
        productScore: {
            score?: number | null;
            param: string;
            paramId: string;
            sustainabilityCategory?: 'low' | 'medium' | 'high';
            sustainabilityScore?: number;
        }[];
    };
    sustainabilityInfoApplicable: boolean;
    contactInformations?: {
        typeName: string;
        name: string;
        address: string;
        channels: {
            code: string;
            value: string;
            displayName: string;
        }[];
    }[];
    claimReportingInformations?: {
        description?: {
            value: string;
            languageCode: string;
        }[];
        claimDetails: {
            type: string;
            name: string;
            elements: {
                code: string;
                name: string;
                markedOnPackage: boolean;
            }[];
        }[];
    };
}

export interface ApiNutritionFact {
    name: string;
    value: string;
    unit?: string;
    percentageOfDailyIntake?: string;
    amountAccuracy?: { code: string; value: string };
}

export interface ApiProductAttribute {
    name: string;
    image?: ApiCloudinaryImage;
    identifier: string;
}

interface ApiProductSize {
    size?: number;
    unit: string;
    packageSizeInformation?: string;
}

export interface ApiProductCatchData {
    catchMethod: string;
    catchArea: string;
}

export interface ApiPriceData {
    inclTaxPrice?: number;
    exclTaxPrice?: number;
}

export interface ApiConsumerInstructions {
    usageInstructions?: string;
    storageInstructions?: string;
}

export interface ApiCountry {
    code: string;
    value: string;
}

export type ComparativePriceUnit = 'kg' | 'st' | 'lit' | (string & {});
